import { Box, Flex } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { CloseIcon, PlusIcon } from 'Asset/icon';
import { Condition } from 'Component/others';
import useGetAllRoles from 'Domain/role/useGetAllRoles';
import useGetBrand from 'Domain/brand/useGetBrand';
import useGetEcommerce from 'Domain/ecommerce/useGetEcommerce';
import { getMenuPermissions } from 'Helper/getMenuPermissions';
import ButtonSolid from 'UiKit/button/buttonSolid';
import FilterSelect from 'UiKit/filter-select';
import SearchComponent from 'UiKit/search';
import { TextSemiBold } from 'UiKit/typography';

import checkIsResetAllFilter from 'View/user/helper/checkIsResetAllFilter';
import { useUserState } from 'View/user/state';

import styles from './styles.module.css';

const Filter = () => {
  const { t } = useTranslation('user');
  const userPermission = getMenuPermissions('master-data', 'user');
  const navigate = useNavigate();

  const optionsRole = useGetAllRoles();
  const optionsBrand = useGetBrand();
  const optionsEcommerce = useGetEcommerce({ enabled: true });

  const { filterState, updateFilterState, resetFilterState } = useUserState();
  const isResetAllFilter = checkIsResetAllFilter(filterState);

  const onChangeFilter = (newFilter) => updateFilterState({ ...newFilter, page: 1 });
  const onResetFilter = () => resetFilterState();

  const statusOptions = [
    { value: 'ACTIVE', label: t('filterStatusActive') },
    { value: 'INACTIVE', label: t('filterStatusInactive') },
  ];

  return (
    <Box className={styles['filter__wrapper']}>
      <Flex className={styles['filter__container']}>
        <Flex className={styles['filter-item__container']}>
          <Flex flex={1} gap={4}>
            <SearchComponent maxWidth="337px" value={filterState?.search} onSearch={(value) => onChangeFilter({ search: value })} />

            <Box className={styles['select__container']}>
              <FilterSelect
                value={filterState?.role}
                onChange={(option) => onChangeFilter({ role: option })}
                placeholder={t('filterRolePlaceholder')}
                clearable={true}
                isMulti={true}
                counterLabel={t('filterRoleCounter')}
                width="168px"
                options={optionsRole?.data}
                isLoading={optionsRole?.isLoading}
              />
            </Box>

            <Box className={styles['select__container']}>
              <FilterSelect
                value={filterState?.brand}
                onChange={(option) => onChangeFilter({ brand: option })}
                placeholder={t('filterBrandPlaceholder')}
                clearable={true}
                isMulti={true}
                counterLabel={t('filterBrandCounter')}
                width="168px"
                options={optionsBrand?.data?.map((brand) => ({
                  value: brand.id,
                  label: brand.label,
                }))}
                isLoading={optionsBrand?.isLoading}
              />
            </Box>

            <Box className={styles['select__container']}>
              <FilterSelect
                value={filterState?.platform}
                onChange={(option) => onChangeFilter({ platform: option })}
                placeholder={t('filterPlatformPlaceholder')}
                clearable={true}
                isMulti={true}
                counterLabel={t('filterPlatformCounter')}
                width="168px"
                options={optionsEcommerce?.data}
                isLoading={optionsEcommerce?.isLoading}
              />
            </Box>

            <Box className={styles['select__container']}>
              <FilterSelect
                value={filterState?.status}
                onChange={(option) => onChangeFilter({ status: option })}
                placeholder={t('filterStatusPlaceholder')}
                width="168px"
                options={statusOptions}
              />
            </Box>

            <Condition statement={isResetAllFilter}>
              <Flex onClick={onResetFilter} className={styles['reset-filter__container']}>
                <CloseIcon size="14px" color="var(--color-semantic-text)" />
                <TextSemiBold>{t('clearFilterButton')}</TextSemiBold>
              </Flex>
            </Condition>
          </Flex>

          <Condition statement={userPermission?.isCanCreate}>
            <ButtonSolid
              type="button"
              className={styles['create__button']}
              leftIcon={<PlusIcon width="14px" height="14px" mt="2px" color="var(--color-semantic-white)" />}
              onClick={() => navigate('/master-data/user/upsert?viewType=upsert')}
            >
              {t('createButton')}
            </ButtonSolid>
          </Condition>
        </Flex>
      </Flex>
    </Box>
  );
};

export default Filter;